import { Component, computed, inject, input, model, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Fluid } from 'primeng/fluid';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { CompanyService } from '@/app/services/company.service';
import { CountryService } from '@/app/services/country.service';
import { AuthService } from '@/app/services/auth.service';
import { RealmConfig, RealmSupportConfig } from '@/lib-backend';
import { relativeTime } from '@/app/utils/relative-time';
import { ReassignCompanyDialog } from './reassign-company-dialog';

@Component({
    selector: 'app-website-info-general',
    standalone: true,
    imports: [FormsModule, InputText, Fluid, InputGroup, InputGroupAddon, Button, Tooltip, ReassignCompanyDialog],
    templateUrl: './general-tab.html',
})
export class GeneralTab implements OnInit {
    private readonly companyService = inject(CompanyService);
    protected readonly countryService = inject(CountryService);
    private readonly authService = inject(AuthService);

    /** Reassign + view company list require global admin (backend gating). */
    protected readonly canManageCompany = this.authService.isGlobalAdmin;

    readonly realm = input.required<RealmConfig>();
    readonly draft = model.required<RealmSupportConfig>();
    readonly realmReassigned = output<RealmConfig>();

    protected readonly reassignDialogVisible = signal(false);

    protected readonly currentCompany = computed(() =>
        this.companyService.byId().get(this.realm().CompanyId) ?? null
    );

    protected readonly dirty = computed(() =>
        JSON.stringify(this.draft()) !== JSON.stringify(this.realm().Support)
    );

    protected readonly createdAtRelative = computed(() =>
        relativeTime(this.realm().CreatedAt)
    );

    protected readonly category = computed(() => this.realm().CategorySpecific);

    ngOnInit(): void {
        // Companies are global-admin-only on the backend; only fetch when allowed.
        // The current company is still readable from `RealmConfig.CompanyId`,
        // we just can't lookup its name without the list.
        if (this.canManageCompany() && this.companyService.companies().length === 0) {
            this.companyService.loadCompanies();
        }
    }

    protected setSupportEmail(value: string): void {
        this.draft.set({ SupportEmail: value });
    }

    protected resetAll(): void {
        this.draft.set({ ...this.realm().Support });
    }

    protected openReassign(): void {
        this.reassignDialogVisible.set(true);
    }

    protected onReassigned(config: RealmConfig): void {
        this.realmReassigned.emit(config);
    }
}
